import { call, takeLatest, takeEvery, put, select, debounce } from 'redux-saga/effects';

import {
  getAlarms,
  updateAlarm as updateAlarmApi,
  getEvents,
  deleteAlarm as deleteAlarmApi,
} from '../../services';
import {
  displayNotification,
  checkOnline,
  getNotification,
  resolveCurrentUserConfig,
  getAppEnabledForSeverity,
} from '../notifications';
import { getActiveSite, getSite } from '../sites';
import { REFRESH_VALUES } from '../application';
import config from '../../config';
import {
  FETCH_ALARMS,
  UPDATE_ALARM,
  DELETE_ALARM,
  FETCH_EXPORT_ALARMS,
  FETCH_ALARM_SOURCE_EVENTS,
  FETCH_ALARM_LOGBOOK,
  REQUEST_NOTIFY_ALARMS,
  receiveAlarms,
  alarmUpdated,
  alarmDeleted,
  setAlarmsLoading,
  exportAlarmsDone,
  requestAlarms,
  receiveAlarmSourceEvents,
  setSourceEventsLoading,
  receiveAlarmLogbook,
  setLogbookLoading,
  receiveNotifyAlarms,
} from './actions';
import { getNotifiedAlarmIds } from './selectors';

export function* doFetchAlarms(action) {
  const { query } = action;
  try {
    yield put(setAlarmsLoading(true));
    const { data: alarms, total } = yield call(getAlarms, query);
    yield put(receiveAlarms(alarms, query, total));
  } catch (error) {
    console.error('Unable to fetch alarms:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getAlarms', 'error')()));
  } finally {
    yield put(setAlarmsLoading(false));
  }
}

export function* doUpdateAlarm(action) {
  const { alarmId, data, queryParameters } = action;
  try {
    const alarm = yield call(updateAlarmApi, alarmId, data);
    yield put(alarmUpdated(alarm));
    yield put(displayNotification(getNotification('updateAlarm', 'success')()));
    // Refetch alarms for all states to update the lists
    const site = yield select(getActiveSite);
    if (site) {
      const query = queryParameters
        ? { ...queryParameters, offset: 0 }
        : { org: site.org, site: site.id };
      yield put(requestAlarms({ ...query, state: 'new' }));
      yield put(requestAlarms({ ...query, state: 'acknowledged' }));
      yield put(requestAlarms({ ...query, state: 'resolved' }));
    }
  } catch (error) {
    console.error('Unable to update alarm:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateAlarm', 'error')()));
  }
}

export function* doExportAlarms(action) {
  const { query, exportCallback } = action;
  try {
    const { data: alarms } = yield call(getAlarms, query);
    if (exportCallback) exportCallback(alarms);
  } catch (error) {
    console.error('Unable to export alarms:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('exportAlarms', 'error')()));
  } finally {
    yield put(exportAlarmsDone());
  }
}

/**
 * Fetches DTMP events that constitute an alarm (source events).
 * Uses composite key filters: algorithmId, messageType, additionalIdentifier, deviatingReferenceDesignation
 * within the alarm's firstOccurrence to lastOccurrence time window.
 */
export function* doFetchAlarmSourceEvents(action) {
  const { alarm } = action;
  const {
    id: alarmId,
    algorithmId,
    messageType,
    additionalIdentifier,
    deviatingReferenceDesignation,
    firstOccurrence,
    lastOccurrence,
  } = alarm;

  try {
    yield put(setSourceEventsLoading({ alarmId, loading: true }));

    const site = yield select(getActiveSite);
    if (!site) {
      throw new Error('No active site');
    }

    // Build query with composite key filters to find DTMP events that constitute this alarm
    const query = {
      site: config.onPremise ? 'default' : site.id,
      eventType: 'dtmpEvent',
      from: new Date(firstOccurrence).getTime(),
      to: new Date(lastOccurrence).getTime(),
      order: 'desc',
    };

    // Only add filters if they have valid values
    if (algorithmId) {
      query.algorithmId = algorithmId;
    }
    if (messageType) {
      query.messageType = messageType;
    }
    if (additionalIdentifier) {
      query.additionalIdentifier = additionalIdentifier;
    }
    if (deviatingReferenceDesignation) {
      query.deviatingReferenceDesignations = [deviatingReferenceDesignation];
    }

    const { values: events, total } = yield call(getEvents, query);
    yield put(receiveAlarmSourceEvents({ alarmId, events, total }));
  } catch (error) {
    console.error('Unable to fetch alarm source events:', error);
    yield call(checkOnline);
    yield put(receiveAlarmSourceEvents({ alarmId, events: [], total: 0 }));
  } finally {
    yield put(setSourceEventsLoading({ alarmId, loading: false }));
  }
}

/**
 * Fetches previously resolved alarms of the same type for the logbook tab.
 * Uses composite key fields (messageType, additionalIdentifier, deviatingReferenceDesignation)
 * to find alarms of the same type that have been resolved.
 * Note: We don't include algorithmId because that would only match the exact same alarm instance.
 * The "same type" means same equipment/location/message type combination.
 * On-premise alarms are stored with fixed org/site placeholders, so org/site filters are omitted there.
 */
export function* doFetchAlarmLogbook(action) {
  const { alarm } = action;
  const { id: alarmId, messageType, additionalIdentifier, deviatingReferenceDesignation } = alarm;

  try {
    yield put(setLogbookLoading({ alarmId, loading: true }));

    const site = yield select(getActiveSite);
    if (!site) {
      throw new Error('No active site');
    }

    // Build query using composite key fields (without algorithmId) to find alarms of the same type
    const query = {
      ...(config.onPremise ? {} : { org: site.org, site: site.id }),
      state: 'resolved',
    };

    // Add composite key filters if they have valid values
    if (messageType) {
      query.messageType = messageType;
    }
    if (additionalIdentifier) {
      query.additionalIdentifier = additionalIdentifier;
    }
    if (deviatingReferenceDesignation) {
      query.deviatingReferenceDesignation = deviatingReferenceDesignation;
    }

    const { data: alarms } = yield call(getAlarms, query);

    // Filter out the current alarm if it's in the results
    const filteredAlarms = alarms.filter((a) => a.id !== alarmId);

    yield put(
      receiveAlarmLogbook({ alarmId, alarms: filteredAlarms, total: filteredAlarms.length })
    );
  } catch (error) {
    console.error('Unable to fetch alarm logbook:', error);
    yield call(checkOnline);
    yield put(receiveAlarmLogbook({ alarmId, alarms: [], total: 0 }));
  } finally {
    yield put(setLogbookLoading({ alarmId, loading: false }));
  }
}

export function* doDeleteAlarm(action) {
  const { alarmId } = action;
  try {
    yield call(deleteAlarmApi, alarmId);
    yield put(alarmDeleted(alarmId));
    yield put(displayNotification(getNotification('deleteAlarm', 'success')()));
  } catch (error) {
    console.error('Unable to delete alarm:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteAlarm', 'error')()));
  }
}

const SEVERITY_STATUS_MAP = {
  Alert: 'alert',
  Warning: 'warning',
  Normal: 'normal',
};

const ALL_SEVERITIES = Object.keys(SEVERITY_STATUS_MAP);
const ON_PREM_SEVERITIES = new Set(['Warning', 'Alert']);

const normalizeSeverity = (severity) => {
  if (!severity) return 'Normal';
  const lower = severity.toLowerCase().replace(/s$/, '');
  if (lower === 'alert') return 'Alert';
  if (lower === 'warning') return 'Warning';
  return 'Normal';
};

export function* doRequestNotifyAlarms(action) {
  const { siteId } = action;
  const site = siteId
    ? yield select((state) => getSite(state, siteId))
    : yield select(getActiveSite);
  if (!site?.id) return;

  // Determine which severities should produce notifications:
  // On-prem: only Warning and Alert (no config system)
  // Cloud: based on user notification config, skip fetch if all disabled
  let enabledSeverities;
  if (config.onPremise) {
    enabledSeverities = ON_PREM_SEVERITIES;
  } else {
    const userConfig = yield call(resolveCurrentUserConfig, site);
    enabledSeverities = new Set(
      ALL_SEVERITIES.filter((sev) => getAppEnabledForSeverity(userConfig, sev))
    );
    if (enabledSeverities.size === 0) {
      return;
    }
  }

  const notifiedAlarmIds = yield select(getNotifiedAlarmIds);

  try {
    const baseQuery = { org: site.org, site: site.id };
    const { data: newAlarms } = yield call(getAlarms, {
      ...(config.onPremise ? {} : baseQuery),
      state: 'new',
      limit: 1000, // Fetch up to 1000 new alarms for notifications
    });
    const allAlarms = newAlarms || [];

    // Only keep IDs that are still in the new state so acknowledged alarms
    // are forgotten and re-notified if they return to new (e.g. escalation)
    const updatedIds = {};
    const toNotify = [];

    for (const alarm of allAlarms) {
      if (!notifiedAlarmIds[alarm.id]) {
        const severity = normalizeSeverity(alarm.latestSeverity);
        if (enabledSeverities.has(severity)) {
          toNotify.push(alarm);
        }
      }
      updatedIds[alarm.id] = true;
    }

    yield put(receiveNotifyAlarms({ notifiedAlarmIds: updatedIds }));

    // Group notifications by severity and display
    if (toNotify.length > 0) {
      const bySeverity = {};
      for (const alarm of toNotify) {
        const sev = normalizeSeverity(alarm.latestSeverity);
        bySeverity[sev] = (bySeverity[sev] || 0) + 1;
      }

      for (const [severity, count] of Object.entries(bySeverity)) {
        yield put(
          displayNotification({
            title: `${count} new ${severity}${count > 1 ? 's' : ''} in Alarms`,
            status: SEVERITY_STATUS_MAP[severity] || 'normal',
            type: 'persistent',
            category: 'alarms',
          })
        );
      }
    }
  } catch (error) {
    console.error('Unable to fetch alarms for notifications:', error);
    yield call(checkOnline);
  }
}

export const sagas = [
  takeEvery(FETCH_ALARMS, doFetchAlarms),
  takeLatest(UPDATE_ALARM, doUpdateAlarm),
  takeLatest(DELETE_ALARM, doDeleteAlarm),
  takeLatest(FETCH_EXPORT_ALARMS, doExportAlarms),
  takeLatest(FETCH_ALARM_SOURCE_EVENTS, doFetchAlarmSourceEvents),
  takeLatest(FETCH_ALARM_LOGBOOK, doFetchAlarmLogbook),
  takeLatest(REQUEST_NOTIFY_ALARMS, doRequestNotifyAlarms),
  debounce(500, REFRESH_VALUES, doRequestNotifyAlarms),
];
